import { useEffect } from "react";

import { useCheckUser } from "@/api/user/check-user";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import useUpdateQueryParams from "@/hooks/use-update-query-params";
import useGlobalLoading from "@/store/global-loading";
import useOnboardingData from "@/store/onboarding-data";

import Step1 from "./step-1";
import Step2 from "./step-2";
import Step3 from "./step-3";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router";

const FirstSetup = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const { isInitialized } = useOnboardingData();

    const updateSearchParams = useUpdateQueryParams();

    const { data, isLoading: checkUserLoading } = useCheckUser();

    const { setIsLoading } = useGlobalLoading();

    // return <>Huga luga buga</>;

    useEffect(() => {
        if (!checkUserLoading) {
            setIsLoading(false); // Avoid unnecessary re-renders
        }

        const firstVisit = searchParams.get("first_visit");
        const step = searchParams.get("step");

        if (isInitialized && data && !checkUserLoading) {
            if (firstVisit) {
                updateSearchParams({ first_visit: undefined, step: undefined });
            }
        }

        if (!isInitialized && data && !checkUserLoading) {
            const newParams: Record<string, string | undefined> = {};

            if (!firstVisit) {
                newParams.first_visit = "true";
            }

            if (!step) {
                newParams.step = "1";
            }

            // Only update if there are changes
            if (Object.keys(newParams).length > 0) {
                updateSearchParams(newParams);
            }
        }
    }, [isInitialized, data, checkUserLoading, setIsLoading]);

    return (
        <div>
            <Card className="mt-4 p-4 text-gray-700">
                <h3 className="text-xl font-bold">Setup Guide</h3>
                <p>Follow these steps to get the maximum out of your AI chat</p>
                <Separator className="my-4" />
                <div className="relative mx-auto mb-10 mt-6 w-full">
                    <h3 className="text-center text-base font-semibold">Step {searchParams.get("step") || 1}</h3>
                    {searchParams.get("step") === "1" && <p className="mb-4 text-center text-xl font-semibold">Web Settings</p>}
                    {searchParams.get("step") === "2" && <p className="mb-4 text-center text-xl font-semibold">Knowledge Base</p>}
                    {searchParams.get("step") === "3" && <p className="mb-4 text-center text-xl font-semibold">Chat Settings</p>}
                    <div className="flex items-center justify-between">
                        <Link
                            to={"/app/dashboard?step=1&first_visit=true"}
                            className={cn(
                                "relative z-10 flex w-8 items-center justify-center gap-2 rounded-full border  bg-white p-2 ",
                                searchParams.get("step") === "1" && "border-blue-500 text-blue-700",
                                searchParams.get("step") === "2" && "border-blue-500 bg-blue-500 text-white",
                                searchParams.get("step") === "3" && "border-blue-500 bg-blue-500 text-white"
                            )}
                        >
                            <p className="text-xs">1</p>
                        </Link>
                        <div
                            className={cn(
                                "absolute left-5 w-1/2 min-w-11 border border-gray-300",
                                (searchParams.get("step") === "2" || searchParams.get("step") === "3") && "border-blue-500"
                            )}
                        ></div>
                        <Link
                            to={"/app/dashboard?step=2&first_visit=true"}
                            className={cn(
                                "relative z-10 flex w-8 items-center justify-center gap-2 rounded-full border border-gray-300 bg-white p-2 text-gray-600",
                                searchParams.get("step") === "2" && "border-blue-500 text-blue-700",
                                searchParams.get("step") === "3" && "border-blue-500 bg-blue-500 text-white"
                            )}
                        >
                            <p className="text-xs">2</p>
                        </Link>
                        <div
                            className={cn(
                                "absolute left-1/2 w-1/2 min-w-11 border border-gray-300",
                                searchParams.get("step") === "3" && "border-blue-500"
                            )}
                        ></div>
                        <Link
                            to={"/app/dashboard?step=3&first_visit=true"}
                            className={cn(
                                "relative z-10 flex w-8 items-center justify-center gap-2 rounded-full border border-gray-300 bg-white p-2 text-gray-600",
                                searchParams.get("step") === "3" && "border-blue-500 text-blue-700"
                            )}
                        >
                            <p className="text-xs">3</p>
                        </Link>
                    </div>
                </div>
                <Separator />
                {searchParams.get("step") === "1" && <Step1 />}
                {searchParams.get("step") === "2" && <Step2 />}
                {searchParams.get("step") === "3" && <Step3 />}
            </Card>
        </div>
    );
};

export default FirstSetup;
